import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { QRCodeSVG } from 'qrcode.react'
import logo from '@/assets/images/logo-reversed.svg'
import { primaryColors } from '@/assets/styles/helpers/colors.styles'
import styles from './RegisterDeviceModal.module.scss'
import Chip from '@/shared/components/Chip/Chip'
import { useEffect, useState } from 'react'
import TextField from '@/shared/components/TextField/TextField'
import Select from '@/shared/components/Select/Select'
import Button from '@/shared/components/Button/Button'
import Loader from '@/shared/components/Loader/Loader'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { socketService } from '@/shared/services/socket.service'
import { WebSocketEvent } from '@/shared/services/webSocketEvent'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { auth } from '@/features/auth/firebaseConfig'

interface RegisterDeviceModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  userOptions?: OptionType[]
  existingDeviceNames?: string[]
  editMode?: boolean
  defaultDeviceName?: string
  defaultUsers?: OptionType[]
  onSubmit?: (deviceName: string, users: OptionType[]) => void
  onRegister?: (
    userIds: string[],
    label: string
  ) => Promise<{ url: string; code: string }>
  onDeviceRegistered?: () => void
}

function RegisterDeviceModal(props: RegisterDeviceModalProps) {
  const { t } = useTranslation()
  const {
    isOpen,
    setIsOpen,
    userOptions = [],
    existingDeviceNames = [],
    editMode = false,
    defaultDeviceName = '',
    defaultUsers = [],
    onSubmit,
    onRegister,
    onDeviceRegistered
  } = props
  const { showSnackbar } = useSnackbar()
  const handleOnClose = () => setIsOpen(false)
  const [registered, setRegistered] = useState(false)
  const [registrationUrl, setRegistrationUrl] = useState('')
  const [registrationCode, setRegistrationCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [deviceName, setDeviceName] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<OptionType[]>([])
  const [deviceNameError, setDeviceNameError] = useState('')
  const [userError, setUserError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setRegistered(false)
      setDeviceName(defaultDeviceName)
      setSelectedUsers(defaultUsers)
      setDeviceNameError('')
      setUserError('')
      setRegistrationUrl('')
      setRegistrationCode('')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (!registered || !isOpen || !auth.currentUser) return
    const handleDeviceChanged = (data: unknown) => {
      console.log('[WebSocket] DEVICE_CHANGED received:', data)
      showSnackbar(
        t('ALERTS.REGISTER_DEVICE_SUCCESS.TITLE'),
        t('ALERTS.REGISTER_DEVICE_SUCCESS.DESCRIPTION'),
        'success'
      )
      onDeviceRegistered?.()
      socketService.disconnect()
      setIsOpen(false)
    }
    auth.currentUser.getIdToken().then((token: string) => {
      console.log('[WebSocket] Connecting and subscribing...')
      socketService.connect(token, WebSocketEvent.DEVICES_SUBSCRIBE)
      socketService.on(WebSocketEvent.DEVICES_CHANGED, handleDeviceChanged)
      console.log('[WebSocket] Listening for DEVICE_CHANGED')
    })
    return () => {
      socketService.off(WebSocketEvent.DEVICES_CHANGED, handleDeviceChanged)
      socketService.disconnect()
    }
  }, [registered, isOpen])

  const validateForm = () => {
    let valid = true
    if (!deviceName.trim()) {
      setDeviceNameError(t('REGISTER_DEVICE_MODAL.FORM.NAME_REQUIRED'))
      valid = false
    } else if (
      existingDeviceNames
        .filter(name => name !== defaultDeviceName)
        .includes(deviceName.trim())
    ) {
      setDeviceNameError(t('REGISTER_DEVICE_MODAL.FORM.NAME_UNIQUE'))
      valid = false
    } else {
      setDeviceNameError('')
    }
    if (selectedUsers.length === 0) {
      setUserError(t('REGISTER_DEVICE_MODAL.FORM.USER_REQUIRED'))
      valid = false
    } else {
      setUserError('')
    }
    return valid
  }

  const handleGenerateQr = async () => {
    if (!validateForm() || !onRegister) return
    setIsLoading(true)
    const minDelay = new Promise(resolve => setTimeout(resolve, 2000))
    let result: { url: string; code: string } | null = null
    try {
      const [apiResult] = await Promise.all([
        onRegister(
          selectedUsers.map(u => u.value),
          deviceName.trim()
        ),
        minDelay
      ])
      result = apiResult
    } catch {
      await minDelay
      setIsLoading(false)
      return
    }
    setRegistrationUrl(result.url)
    setRegistrationCode(result.code)
    setIsLoading(false)
    setRegistered(true)
  }

  const handleEditConfirm = () => {
    if (!validateForm()) return
    onSubmit?.(deviceName.trim(), selectedUsers)
    setIsOpen(false)
  }

  return (
    <Modal
      title={
        editMode
          ? t('REGISTER_DEVICE_MODAL.EDIT_TITLE')
          : t('REGISTER_DEVICE_MODAL.TITLE')
      }
      size="large"
      onClose={handleOnClose}
      isOpen={isOpen}
    >
      {!registered ? (
        <div className={styles.formPageWrapper}>
          <div className={styles.formPageLeftSideWrapper}>
            <p className={`${styles.formPageDescription} body-medium`}>
              {editMode
                ? t('REGISTER_DEVICE_MODAL.EDIT_DESCRIPTION')
                : t('REGISTER_DEVICE_MODAL.DESCRIPTION')}
            </p>
            <div className={styles.stepWrapper}>
              <Chip label="1." color="primary" />
              <p className={`${styles.qrCodeModalDescription} body-medium`}>
                {t('REGISTER_DEVICE_MODAL.FORM_PAGE.FIRST_STEP')}
              </p>
            </div>
            <div className={styles.formFieldsWrapper}>
              <TextField
                label={t('REGISTER_DEVICE_MODAL.FORM.NAME')}
                placeholder={t('REGISTER_DEVICE_MODAL.FORM.NAME_PLACEHOLDER')}
                value={deviceName}
                onChange={e => {
                  setDeviceName(
                    typeof e === 'string'
                      ? e
                      : (e as React.ChangeEvent<HTMLInputElement>).target.value
                  )
                  setDeviceNameError('')
                }}
                hasError={!!deviceNameError}
                helperText={deviceNameError}
                focused
                required
                disabled={isLoading}
              />
              <Select
                label={t('REGISTER_DEVICE_MODAL.FORM.USER')}
                placeholder={t('REGISTER_DEVICE_MODAL.FORM.USER_PLACEHOLDER')}
                options={userOptions}
                value={selectedUsers}
                onChange={value => {
                  setSelectedUsers(value as OptionType[])
                  setUserError('')
                }}
                isMutiSelect
                hasError={!!userError}
                helperText={userError}
                focused
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.formButtonsWrapper}>
            {isLoading && (
              <div className={styles.loaderOverlay}>
                <Loader size="large" />
              </div>
            )}
            <Button
              label={
                editMode
                  ? t('COMMON.CONFIRM')
                  : t('REGISTER_DEVICE_MODAL.GENERATE_QR')
              }
              variant="contained"
              color="primary"
              onClick={editMode ? handleEditConfirm : handleGenerateQr}
              disabled={isLoading}
            />
          </div>
        </div>
      ) : (
        <div className={styles.qrCodeModalWrapper}>
          <div className={styles.descriptionWrapper}>
            <p className={`${styles.qrCodeModalSubtitle} title-bold-large`}>
              {t('REGISTER_DEVICE_MODAL.SUBTITLE')}
            </p>
            <div className={styles.stepWrapper}>
              <Chip label="1." color="primary" />
              <p className={`${styles.qrCodeModalDescription} body-medium`}>
                {t('REGISTER_DEVICE_MODAL.CODE_PAGE.FIRST_STEP')}
              </p>
            </div>
            <p className={`${styles.qrCodeModalData} title-bold-large`}>
              wos.menupos.app
            </p>
            <div className={styles.stepWrapper}>
              <Chip label="2." color="primary" />
              <p className={`${styles.qrCodeModalDescription} body-medium`}>
                {t('REGISTER_DEVICE_MODAL.CODE_PAGE.SECOND_STEP')}
              </p>
            </div>
            <p className={`${styles.qrCodeModalData} title-bold-large`}>
              {registrationCode
                ? registrationCode.match(/.{1,4}/g)?.join(' - ')
                : '—'}
            </p>
          </div>
          <div className={styles.divider} />
          <div className={styles.codesWrapper}>
            <div className={styles.qrCodeWrapper}>
              <QRCodeSVG
                level="H"
                value={registrationUrl}
                size={256}
                fgColor={primaryColors.black}
                bgColor={primaryColors.white}
                imageSettings={{
                  src: logo,
                  height: 40,
                  width: 40,
                  excavate: true
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default RegisterDeviceModal
